import React, { useEffect, useRef, useState, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import {
  AppUpdate,
  AppUpdateAvailability,
  FlexibleUpdateInstallStatus,
} from "@capawesome/capacitor-app-update";
import useAccentColor from "../hooks/useAccentColor";
import styles from "../styles/UpdateBanner.module.css";

/**
 * Drives Google Play's native in-app update flow on Android.
 *
 *  1. On mount, checks for an available update.
 *  2. If flexible updates are allowed, starts the background download flow
 *     (Play Store shows its own consent dialog — we don't control it).
 *  3. When the download finishes, renders a banner prompting the user to
 *     install. Tapping the button calls `completeFlexibleUpdate()` which
 *     restarts the app onto the new version.
 *
 *  No-ops on iOS and web — there's no Play Store in-app update API for those
 *  platforms, and the existing Firestore-backed UpdateBanner covers them.
 */
const NativeAppUpdateHandler: React.FC = () => {
  const { accent } = useAccentColor();
  const [readyToInstall, setReadyToInstall] = useState(false);
  const [installing, setInstalling] = useState(false);
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    if (!Capacitor.isNativePlatform()) return;
    if (Capacitor.getPlatform() !== "android") return;
    hasRunRef.current = true;

    let listenerHandle: { remove: () => void } | null = null;

    const run = async () => {
      try {
        const info = await AppUpdate.getAppUpdateInfo();

        if (info.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
          setReadyToInstall(true);
          return;
        }

        const inProgress = info.updateAvailability === AppUpdateAvailability.UPDATE_IN_PROGRESS;
        const available = info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE;

        if (!available && !inProgress) return;
        if (!info.flexibleUpdateAllowed) return;

        const added = await AppUpdate.addListener("onFlexibleUpdateStateChange", (state) => {
          if (state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
            setReadyToInstall(true);
          }
        });
        listenerHandle = added;

        if (available) {
          await AppUpdate.startFlexibleUpdate().catch((err) => {
            console.warn("[AppUpdate] startFlexibleUpdate failed:", err);
          });
        }
      } catch (err) {
        console.warn("[AppUpdate] getAppUpdateInfo failed:", err);
      }
    };

    run();

    return () => {
      if (listenerHandle) listenerHandle.remove();
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (installing) return;
    setInstalling(true);
    try {
      await AppUpdate.completeFlexibleUpdate();
    } catch (err) {
      console.warn("[AppUpdate] completeFlexibleUpdate failed:", err);
      setInstalling(false);
    }
  }, [installing]);

  if (!readyToInstall) return null;

  return (
    <div className={styles.banner} style={{ backgroundColor: accent, color: "#fff" }}>
      <div className={styles.content}>
        <i className="fa-solid fa-circle-check" style={{ marginRight: 8 }} />
        <span>Update downloaded — restart to install</span>
      </div>
      <div className={styles.actions}>
        <button
          className={styles.updateBtn}
          onClick={handleInstall}
          style={{ color: accent }}
          disabled={installing}
        >
          {installing ? "Installing…" : "Restart"}
        </button>
      </div>
    </div>
  );
};

export default NativeAppUpdateHandler;
